import FormField from '@ingka/form-field'
import InlineMessage from '@ingka/inline-message'
import Select, { Option } from '@ingka/select'
import { space200, space400 } from '@ingka/variables'
import { Box, Grid } from '@mantine/core'
import { orderBy } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'

import { QaMetricId } from 'config/domain/qa.config'

import { CoachingSession } from 'lib/types/coachingSession.types'
import { Mission } from 'lib/types/mission.types'
import { QaType } from 'lib/types/qualityAssessment.types'

import { useFormatDate } from 'hooks/formatting'
import { useCurrentQaDataForCoworker } from 'hooks/qualityAssssments/useCurrentQaDataForCoworker.hooks'

import { useCoworker } from 'state/slices/api'

import { LoadingIndicator } from 'components/composites/Shared/LoadingIndicator'
import { Widget } from 'components/composites/Shared/Widget'
import { MissionDraftEditor } from 'components/features/Missions/MissionDraftEditor'
import { MissionQaMetricsTable } from 'components/features/Missions/MissionQaMetricsTable'
import { MissionQaMetricsFormProps } from 'components/features/Missions/MissionQaMetricsTable/MissionQaMetricsTable.types'
import QaNotes from 'components/features/QualityAssessments/QaNotes.component'
import { NumberedHeader } from 'components/primitives/NumberedHeader'
import { Label, SubHeading } from 'components/primitives/Text'

import { useCurrentSessionData } from './ReviewMissions.hooks'

type ReviewMissionsProps = {
  coworkerId: string
  coachingSession: CoachingSession
}

const ReviewMissions: React.FC<ReviewMissionsProps> = ({ coworkerId }) => {
  const location = useLocation()
  const coachingState = location.pathname.split('/').slice(-1)[0]
  const { t } = useTranslation()
  const { coachingSessionId } = useParams()
  const qaSelectForm = useForm<MissionQaMetricsFormProps>({
    defaultValues: {
      qaId: undefined,
    },
  })

  const qaData = useCurrentQaDataForCoworker(coworkerId)
  const formatDate = useFormatDate()

  const { getValues, resetField } = qaSelectForm
  const { qaId } = getValues()
  const { data: coworker } = useCoworker(coworkerId)

  const qaMetricsBoxRef = useRef<HTMLDivElement>(null)

  const [isChecked, setIsChecked] = useState<boolean>(false)
  const [selectedCheckboxId, setSelectedCheckboxId] = useState<string | null>(null)
  const [mission, setCurrentMission] = useState<Mission>()

  const [assessmentType, setAssessmentType] = useState<QaType>()

  const {
    assessments: assessmentsData,
    missions: missionsData,
    sac,
    sales,
    resolution,
    qbs,
    qfs
  } = useCurrentSessionData(coworkerId)

  useEffect(() => {
    if (assessmentsData.length > 0 && assessmentType === undefined) {
      setAssessmentType(
        orderBy(
          [
            {
              id: 'QAR',
              assessmentNr: resolution.assessments.length,
              missionNr: resolution.missions.length,
            },
            {
              id: 'QAS',
              assessmentNr: sales.assessments.length,
              missionNr: sales.missions.length,
            },
            {
              id: 'QSA',
              assessmentNr: sac.assessments.length,
              missionNr: sac.missions.length,
            },
            {
              id: 'QBS',
              assessmentNr: qbs.assessments.length,
              missionNr: qbs.missions.length,
            },
            {
              id: 'QFS',
              assessmentNr: qfs.assessments.length,
              missionNr: qfs.missions.length,
            },
          ],
          ['assessmentNr', 'missionNr'],
          ['desc', 'desc']
        )[0].id as QaType
      )
    } else if (assessmentType === undefined) {
      setAssessmentType(QaType.QAR)
    }
  }, [assessmentType, sac, sales, resolution, qbs, qfs, assessmentsData])

  const handleTypeChanged = useCallback(
    (value: QaType) => {
      if (!(value === QaType.QAR || value === QaType.QAS || value === QaType.QSA || value === QaType.QBS || value === QaType.QFS)) {
        return
      }
      setAssessmentType(value)
      resetField('qaId')
    },
    [resetField]
  )

  return (
    <>
      {coworker && qaData && (
        <>
          {coachingState === 'edit' && (
            <NumberedHeader
              step={2}
              title={t('features.mission.create-mission.qa-metrics-table.title')}
              subtitle={t('features.mission.create-mission.qa-metrics-table.subtitle')}
            />
          )}

          {/* Assessment type dropdown and QA stats column*/}
          <Widget>
            <Grid>
              <Grid.Col span={{ base: 7, md: 7, sm: 12 }}>
                <>
                  <FormField
                    data-testid="form-field-assessment-type"
                    fieldHelper={{
                      msg: (
                        <Label>
                          {t('features.mission.create-mission.fields.assessment-type.hint')}
                          <br />
                          {t(
                            `features.mission.create-mission.fields.assessment-type.${qaData.isFirstSession ? 'date-hint-initial' : `date-hint`
                            }`,
                            {
                              startDate: formatDate(qaData.startDate),
                              endDate: formatDate(qaData.endDate),
                            }
                          )}
                        </Label>
                      ),
                    }}
                  >
                    <Select
                      label={t('features.mission.create-mission.fields.assessment-type.title')}
                      id="metric"
                      data-testid="input-field-assessment-type"
                      value={assessmentType}
                      onChange={(e) => handleTypeChanged(e.target.value as QaType)}
                    >
                      <Option
                        name={`${t('qa.assessment-type.QAR')} (${resolution.assessments.length} ${t(
                          'common.quality-assessments'
                        )}, ${resolution.missions.length} ${t('common.missions')})`}
                        value="QAR"
                      />
                      <Option
                        name={`${t('qa.assessment-type.QAS')} (${sales.assessments.length} ${t(
                          'common.quality-assessments'
                        )}, ${sales.missions.length} ${t('common.missions')})`}
                        value="QAS"
                      />
                      <Option
                        name={`${t('qa.assessment-type.QSA')} (${sac.assessments.length} ${t(
                          'common.quality-assessments'
                        )}, ${sac.missions.length} ${t('common.missions')})`}
                        value="QSA"
                      />
                      <Option
                        name={`${t('qa.assessment-type.QBS')} (${qbs.assessments.length} ${t(
                          'common.quality-assessments'
                        )}, ${qbs.missions.length} ${t('common.missions')})`}
                        value="QBS"
                      />
                      <Option
                        name={`${t('qa.assessment-type.QFS')} (${qfs.assessments.length} ${t(
                          'common.quality-assessments'
                        )}, ${qfs.missions.length} ${t('common.missions')})`}
                        value="QFS"
                      />
                    </Select>
                  </FormField>
                  <Box mb={space200}>
                    {assessmentsData.length === 0 && (
                      <InlineMessage
                        variant="cautionary"
                        title={t('features.mission.warnings.no-assessments')}
                      />
                    )}
                    {missionsData.length === 0 && (
                      <InlineMessage
                        variant="cautionary"
                        title={t('features.mission.warnings.no-missions')}
                      />
                    )}
                  </Box>
                </>

                {assessmentType ? (
                  <MissionQaMetricsTable
                    coworkerId={coworker.legacyId}
                    assessmentType={assessmentType}
                    coachingSessionId={coachingSessionId!}
                    qaSelectForm={qaSelectForm}
                    mission={mission}
                    setCurrentMission={setCurrentMission}
                    selectedCheckboxId={selectedCheckboxId}
                    setSelectedCheckboxId={setSelectedCheckboxId}
                    isChecked={isChecked}
                    setIsChecked={setIsChecked}
                  />
                ) : (
                  <LoadingIndicator />
                )}
              </Grid.Col>

              <Grid.Col
                span={{ base: 5, md: 5, sm: 12 }}
                pl={space200}
                style={{ position: 'sticky', top: 0 }}
                ref={qaMetricsBoxRef}
              >
                {assessmentType && (
                  <div style={{ position: 'sticky', top: space400 }}>
                    {qaId ? (
                      <SubHeading style={{ marginBottom: space200 }}>
                        {t(`qa.${qaId}`) as string}
                      </SubHeading>
                    ) : (
                      <InlineMessage
                        variant="cautionary"
                        title={t('features.mission.create-mission.avg-score-stats.missing-metric')}
                      />
                    )}
                    {
                      <MissionDraftEditor
                        coworkerId={coworkerId!}
                        coachingSessionId={coachingSessionId!}
                        selectedCheckboxId={selectedCheckboxId as QaMetricId}
                        setIsChecked={setIsChecked}
                      />
                    }
                    <QaNotes coworkerId={coworkerId} assessments={assessmentsData} qaId={qaId} />
                  </div>
                )}
              </Grid.Col>
            </Grid>
          </Widget>
        </>
      )}
    </>
  )
}

export default ReviewMissions
